import { DateTime } from 'luxon'

import api from '@functions/api'

import type { CurrentUserPayload } from '@hooks/useCurrentUser'

import type { CampaignModel } from '@models/types'
import type { ReportDefaultStateType, ReportKeyType } from '@models/reports'

export function queueReportExport(url: string, options: object) {
  return api(url, options)
    .then(({ data }: { data: { queued: true }}) => ({
      success: true as const, data,
    }))
    .catch((data: object) => {
      // @ts-expect-error TS2339
      const errors = data?.response?.data?.errors || ['Error creating report']
      return { success: false as const, errors }
    })
}

interface BuildCampaignReportTitleParams {
  campaign: CampaignModel,
  reportString: string,
}

const buildCampaignReportTitle = (params: BuildCampaignReportTitleParams) => {
  const { campaign, reportString } = params

  if (!campaign) return reportString

  const campaignAddress = campaign?.address?.toLowerCase().replace(/ /g, '_')

  const currentDate = DateTime.now().toFormat('yyyyMMdd')

  return `${campaignAddress}_${reportString}_${currentDate}`
}

export type ReportItem = {
  buildReportTitle?: (params: { campaign: CampaignModel }) => string,
  key: ReportKeyType,
  params: ReportDefaultStateType,
  permission: 'advanced' | 'master',
  strings?: object,
  title: string,
  url: string,
}

export const generateReportItems = (state: ReportDefaultStateType): ReportItem[] => {
  const {
    agency_id,
    brand_id,
    campaign_id,
    campaign_status,
    date_column,
    email,
    end_date,
    provider_id,
    provider_price_list_id,
    publication_edition_id,
    publication_id,
    publication_order_id,
    role_id,
    start_date,
    string,
    user_id,
  } = state

  return [
    {
      key: 'agency_cmf_and_subscriptions',
      params: { email },
      permission: 'advanced',
      title: 'Agency CMF and Subscriptions',
      url: '/reports/agencies/agency_cmf_and_subscriptions',
    },
    {
      buildReportTitle: (
        payload: { campaign: CampaignModel },
      ) => buildCampaignReportTitle({
        ...payload,
        reportString: 'activity_statement',
      }),
      key: 'activity_statement',
      params: { campaign_id },
      permission: 'advanced',
      title: 'Activity Statement',
      url: `/reports/campaigns/${campaign_id}/activity_statement`,
    },
    {
      key: 'advanced_ordered_item_summary',
      params: { brand_id, email },
      permission: 'advanced',
      title: 'Advanced Ordered Item Summary (Max 365 days)',
      url: '/reports/order_items/advanced_ordered_item_summary',
    },
    {
      key: 'advanced_pending_ordered_item_summary',
      params: { brand_id, email },
      permission: 'advanced',
      title: 'Advanced Pending Ordered Item Summary (Max 365 days)',
      url: '/reports/order_items/advanced_pending_ordered_item_summary',
    },
    {
      key: 'artwork_status',
      params: { publication_order_id },
      permission: 'advanced',
      title: 'Artwork Status Report',
      url: '/reports/publication_orders/artwork_status',
    },
    {
      key: 'agencies_turnover',
      params: { email, end_date, start_date },
      permission: 'master',
      title: 'Agency Turnover (Max 31 days)',
      url: '/reports/agencies/turnover',
    },
    {
      key: 'agency_revenue_leakage',
      params: { agency_id },
      permission: 'advanced',
      title: 'Agency Revenue Leakage',
      url: '/reports/agencies/revenue_leakage',
    },
    {
      key: 'agency_subscriptions',
      params: {},
      permission: 'advanced',
      title: 'Agency Subscriptions',
      url: '/reports/agencies/agency_subscriptions',
    },
    {
      key: 'approved_orders',
      params: { provider_id },
      permission: 'advanced',
      title: 'Approved Orders (Max 31 days)',
      url: '/reports/providers/approved_orders',
    },
    {
      key: 'approved_order_summary',
      params: { email },
      permission: 'advanced',
      title: 'Approved Order Summary (Max 120 days)',
      url: '/reports/orders/approved_order_summary',
    },
    {
      key: 'order_totals_provider',
      params: { creditor_type: 'provider', date_column },
      permission: 'advanced',
      title: 'Approved Order Totals (Provider)',
      url: '/reports/orders/creditor_totals',
    },
    {
      key: 'order_totals_publication',
      permission: 'advanced',
      params: {
        creditor_type: 'publication',
        date_column,
      },
      title: 'Approved Order Totals (Publication)',
      url: '/reports/orders/creditor_totals',
    },
    {
      key: 'brand_invoices_total',
      params: { end_date, provider_id, start_date },
      permission: 'advanced',
      strings: {
        labels: {
          provider: 'Provider (Leave blank for System)',
        },
      },
      title: 'Brand Invoice Total',
      url: '/reports/invoices/brand_invoices_total',
    },
    {
      key: 'campaigns_with_approved_orders',
      params: { end_date, start_date },
      permission: 'advanced',
      title: 'Campaigns with Approved Orders (Max 31 days)',
      url: '/reports/campaigns/campaigns_with_approved_orders',
    },
    {
      key: 'campaign_summary',
      params: { email },
      permission: 'advanced',
      title: 'Campaign Summary (Max 120 days)',
      url: '/reports/campaigns/campaign_summary',
    },
    {
      buildReportTitle: (
        payload: { campaign: CampaignModel },
      ) => buildCampaignReportTitle({ ...payload, reportString: 'cost_of_sale' }),
      key: 'cost_of_sale',
      params: { campaign_id },
      permission: 'advanced',
      title: 'Cost Of Sale',
      url: `/reports/campaigns/${campaign_id}/cost_of_sale`,
    },
    {
      key: 'digital_vs_print',
      params: { end_date, start_date },
      permission: 'advanced',
      title: 'Digital vs Print Spend (Max 31 days)',
      url: '/reports/order_items/digital_vs_print',
    },
    {
      key: 'installed_signboards',
      params: {},
      permission: 'advanced',
      title: 'Installed Signboards',
      url: '/reports/order_items/installed_signboards',
    },
    {
      key: 'media_publications_billing',
      params: { end_date, provider_id, start_date },
      permission: 'advanced',
      title: 'Media Publications Billing',
      url: '/reports/order_items/media_publications_billing',
    },
    {
      key: 'no_approved_orders',
      params: {},
      permission: 'advanced',
      title: 'No approved orders',
      url: '/reports/agencies/no_approved_orders',
    },
    {
      key: 'off_market_sales',
      params: { end_date, start_date },
      permission: 'master',
      title: 'Off Market Sales (Max 31 days)',
      url: '/reports/campaigns/off_market_sales',
    },
    {
      key: 'ordered_item_overview',
      params: { end_date, provider_id, start_date },
      permission: 'advanced',
      title: 'Ordered Item Overview (Max 31 days)',
      url: '/reports/order_items/ordered_item_overview',
    },
    {
      key: 'ordered_item_summary',
      params: { email },
      permission: 'advanced',
      title: 'Ordered Item Summary (Max 120 days)',
      url: '/reports/order_items/ordered_item_summary',
    },
    {
      key: 'order_reconciliation_by_agency',
      params: { date_column, campaign_status },
      permission: 'advanced',
      title: 'Order Reconciliation (Agency)',
      url: '/reports/orders/order_reconciliation_by_agency',
    },
    {
      key: 'order_reconciliation_by_campaign',
      params: { date_column, campaign_status },
      permission: 'advanced',
      title: 'Order Reconciliation (Campaign)',
      url: '/reports/orders/order_reconciliation_by_campaign',
    },
    {
      key: 'order_reconciliation_by_provider',
      params: { agency_id, provider_id },
      permission: 'advanced',
      title: 'Order Reconciliation (Provider)',
      url: '/reports/orders/order_reconciliation_by_provider',
    },
    {
      key: 'ordered_product_quantities',
      params: {},
      permission: 'advanced',
      title: 'Ordered Product Quantities (Max 31 days)',
      url: '/reports/order_items/product_quantities',
    },
    {
      key: 'payments_not_received',
      params: {},
      permission: 'advanced',
      title: 'Payments Not Received (Max 31 days)',
      url: '/reports/campaign_contributions/payments_not_received',
    },
    {
      key: 'portal_remapping',
      params: { agency_id },
      permission: 'advanced',
      title: 'Portal Remapping (Campaigns)',
      url: '/reports/campaigns/portal_remapping',
    },
    {
      key: 'price_list_details',
      params: { provider_price_list_id },
      permission: 'advanced',
      title: 'Price List Details',
      url: '/reports/provider_price_lists/price_list_details',
    },
    {
      key: 'price_list_utilisation_report',
      params: { provider_id, end_date, start_date },
      permission: 'advanced',
      title: 'Price List Utilisation Report (Max 1 year)',
      url: '/reports/provider_price_lists/utilisation_report',
    },
    {
      key: 'processing_fees',
      params: { provider_id },
      permission: 'advanced',
      title: 'Processing Fees',
      url: '/reports/providers/processing_fees',
    },
    {
      key: 'proposals_created_per_month',
      params: {},
      permission: 'advanced',
      title: 'Proposals Created Per Month',
      url: '/reports/campaign_proposals/proposals_created_per_month',
    },
    {
      key: 'provider_packages',
      params: { provider_id },
      permission: 'advanced',
      title: 'Provider Packages',
      url: '/reports/providers/provider_packages',
    },
    {
      key: 'provider_revenue_leakage',
      params: { provider_id },
      permission: 'advanced',
      title: 'Provider Revenue Leakage',
      url: '/reports/providers/revenue_leakage',
    },
    {
      key: 'publication_edition_orders',
      params: { publication_edition_id },
      permission: 'advanced',
      title: 'Publication Edition Orders',
      url: '/reports/orders/publication_edition_orders',
    },
    {
      key: 'publication_order_item_summary',
      params: {
        end_date, provider_id, publication_id, start_date,
      },
      permission: 'advanced',
      title: 'Publication Order Summary (Max 31 days)',
      url: '/reports/order_items/publication_order_item_summary',
    },
    {
      key: 'campaign_quote_template_product_usage_report',
      params: {
        brand_id, provider_id,
      },
      permission: 'advanced',
      title: 'Quote Template Item Usage Report (Max 31 days)',
      url: '/reports/campaign_quote_templates/campaign_quote_template_product_usage_report',
    },
    {
      key: 'sales_commission_report',
      params: { end_date, start_date, user_id },
      permission: 'advanced',
      title: 'Sales Commission Report',
      url: '/reports/invoices/sales_commission_report',
    },
    {
      key: 'template_page_usage',
      params: {
        agency_id, brand_id, end_date, start_date,
      },
      permission: 'advanced',
      title: 'Template Page Usage (Max 1 year)',
      url: '/reports/template_pages/usage_report',
    },
    {
      key: 'template_page_usage_advanced',
      params: {
        agency_id, brand_id, end_date, start_date,
      },
      permission: 'advanced',
      title: 'Template Page Usage - Advanced (Max 1 year)',
      url: '/reports/template_pages/usage_report_advanced',
    },
    {
      key: 'template_rejections',
      params: {
        end_date, start_date,
      },
      permission: 'advanced',
      title: 'Template Rejections by Client QC',
      url: '/reports/task_list_items/template_rejections',
    },
    {
      key: 'tracking_links_activity',
      params: { end_date, start_date },
      permission: 'advanced',
      title: 'Tracking Links Activity (Max 31 days)',
      url: '/reports/tracking_links/activity_report',
    },
    {
      key: 'training_lesson_views',
      params: {},
      permission: 'advanced',
      title: 'Training Lesson Views',
      url: '/reports/training_lessons/training_lesson_views',
    },
    {
      key: 'unused_templates',
      params: {},
      permission: 'advanced',
      title: 'Unused Template Pages (Max 1 year)',
      url: '/reports/template_pages/unused_templates',
    },
    {
      key: 'user_details',
      params: {
        agency_id, brand_id, provider_id, role_id, string,
      },
      permission: 'advanced',
      strings: {
        labels: {
          string: 'Filter by Full Name, Email or Username',
        },
      },
      title: 'User Details',
      url: '/reports/users/user_details',
    },
  ]
}

export const getReportingPageAllowedReports = (currentUserPayload: CurrentUserPayload): ReportKeyType[] => {
  const {
    isAdmin, isAgencyAdmin, isBrandUser, isProviderUser,
  } = currentUserPayload

  if (isBrandUser){
    return [
      'advanced_ordered_item_summary', 'advanced_pending_ordered_item_summary', 'approved_order_summary',
      'campaign_summary', 'campaign_quote_template_product_usage_report', 'digital_vs_print', 'off_market_sales',
      'ordered_item_summary', 'order_reconciliation_by_agency', 'order_reconciliation_by_campaign',
      'order_totals_provider', 'order_totals_publication', 'ordered_product_quantities',
      'payments_not_received', 'template_page_usage', 'template_page_usage_advanced', 'tracking_links_activity',
      'unused_templates']
  }

  if (isProviderUser){
    return ['installed_signboards', 'price_list_utilisation_report', 'provider_packages']
  }

  if (isAdmin){
    return ['agency_cmf_and_subscriptions', 'agencies_turnover', 'approved_orders', 'agency_revenue_leakage',
      'brand_invoices_total', 'campaigns_with_approved_orders', 'agency_subscriptions', 'installed_signboards',
      'media_publications_billing', 'no_approved_orders', 'ordered_item_overview', 'price_list_utilisation_report',
      'portal_remapping', 'processing_fees', 'provider_packages', 'publication_order_item_summary',
      'provider_revenue_leakage', 'sales_commission_report', 'template_page_usage', 'template_rejections',
      'proposals_created_per_month', 'training_lesson_views', 'user_details']
  }

  if (isAgencyAdmin){
    return ['activity_statement', 'approved_order_summary', 'campaign_summary',
      'campaign_quote_template_product_usage_report', 'cost_of_sale', 'digital_vs_print',
      'off_market_sales', 'order_totals_provider', 'ordered_item_summary', 'order_totals_publication',
      'ordered_product_quantities', 'order_reconciliation_by_agency', 'order_reconciliation_by_campaign',
      'order_reconciliation_by_provider', 'payments_not_received', 'template_page_usage',
      'template_page_usage_advanced', 'tracking_links_activity', 'unused_templates']
  }

  return []
}

export default queueReportExport
